import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Fuse from "fuse.js";
import "../Nav/nav.css";
// Import CTAButtonMob if needed elsewhere in SearchBar, but not used here
// import CTAButtonMob from "../CTA/CTAButtonMob.jsx";

// Import your configuration objects
import guidesConfig from "../../config/guidesConfig";
import pageConfig from "../../config/pageConfig";

// Build arrays for calculators and guides from your configs,
// ensuring that each object has a 'keywords' field that defaults to
// { primary: [], secondary: [] } if not provided.
const allCalculators = Object.keys(pageConfig).map((key) => {
  const config = pageConfig[key];
  return {
    ...config,
    keywords: {
      primary: (config.keywords && config.keywords.primary) || [],
      secondary: (config.keywords && config.keywords.secondary) || [],
    },
  };
});

const allGuides = Object.keys(guidesConfig).map((key) => {
  const config = guidesConfig[key];
  return {
    ...config,
    keywords: {
      primary: (config.keywords && config.keywords.primary) || [],
      secondary: (config.keywords && config.keywords.secondary) || [],
    },
  };
});

function getBoost(result) {
    // Here we assume priority can be 1, 2, 3, or not set.
    // A result with priority 1 should get a 20% boost,
    // so we multiply its score by 0.8 (i.e. lower score).
    // For priority 2, use 0.9 (10% boost).
    // For priority 3 or undefined, use 1 (no boost).
    const p = result.item.priority;
    console.log(result)
    if (p === 1) return 0.8;
    if (p === 2) return 0.9;
    return 1.0;
  }
  
  const sortByPriority = (a, b) => {
    const aScoreBoosted = a.score * getBoost(a);
    const bScoreBoosted = b.score * getBoost(b);
    return aScoreBoosted - bScoreBoosted;
  };
  

const fuseCalc = new Fuse(allCalculators, {
    keys: [
      { name: "fullTitle", weight: 10 },
      { name: "seo.title", weight: 2 },
      { name: "keywords.primary", weight: 5 },
      { name: "keywords.secondary", weight: 3 },
    ],
    threshold: 0.3,
    minMatchCharLength: 2,
    sortFn: sortByPriority,  // We'll define this next.
  });
  

const fuseGuide = new Fuse(allGuides, {
  keys: [
    { name: "label", weight: 4 },
    { name: "seo.title", weight: 10 },
    { name: "seo.description", weight: 1 },
    { name: "keywords.primary", weight: 5 },
    { name: "keywords.secondary", weight: 3 },
  ],
  threshold: 0.3,
  minMatchCharLength: 2,
  sortFn: sortByPriority,
});

// The rest of your SearchBar component stays the same.
const SearchBar = ({ autoClose = true, onResultClick }) => {
  const [query, setQuery] = useState("");
  const [filteredCalcs, setFilteredCalcs] = useState([]);
  const [filteredGuides, setFilteredGuides] = useState([]);
  const [inputFocused, setInputFocused] = useState(false);
  const [panelHovered, setPanelHovered] = useState(false);

  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);

    if (val.length < 2) {
      setFilteredCalcs([]);
      setFilteredGuides([]);
      return;
    }

    const calcResults = fuseCalc.search(val);
    const guideResults = fuseGuide.search(val);

    setFilteredCalcs(calcResults.map((result) => result.item));
    setFilteredGuides(guideResults.map((result) => result.item));
  };

  const handleResultClick = () => {
    if (autoClose) {
      setQuery("");
      setFilteredCalcs([]);
      setFilteredGuides([]);
    }
    if (typeof onResultClick === "function") {
      onResultClick();
    }
  };

  const showPanel =
    query.length >= 2 && (autoClose ? (inputFocused || panelHovered) : true);

  return (
    <div className="search-bar-container">
      <div className="search-input-container">
        {/* Search icon and input as before */}
        <Search className="search-icon" size={20} color="var(--text2)" />
        <input
          type="text"
          className="search-input"
          placeholder="search"
          value={query}
          onChange={handleSearch}
          onFocus={() => {
            if (autoClose) setInputFocused(true);
          }}
          onBlur={() => {
            if (autoClose) setInputFocused(false);
          }}
        />
      </div>
      {showPanel && (
        <div
          className="search-panel"
          onMouseEnter={() => {
            if (autoClose) setPanelHovered(true);
          }}
          onMouseLeave={() => {
            if (autoClose) setPanelHovered(false);
          }}
        >
          {filteredCalcs.length === 0 && filteredGuides.length === 0 ? (
            <div className="no-results-container">
              <h2 className="no-results-heading">We didn't find anything</h2>
              {/* Optionally include CTAButtonMob here */}
            </div>
          ) : (
            <>
              {filteredCalcs.length > 0 && (
                <div className="search-panel-section">
                  <h2 className="search-section-title">Related Calculators</h2>
                  <div className="calculator-grid">
                    {filteredCalcs.slice(0, 6).map((calc, idx) => (
                      <Link
                        key={idx}
                        to={calc.route}
                        className="grid-item"
                        onClick={handleResultClick}
                      >
                        <div className="icon">{calc.sidebar?.icon}</div>
                        <div className="label">
                          {calc.fullTitle || calc.seo.title}
                        </div>
                        {calc.sidebar?.sub && (
                          <div className="sub">{calc.sidebar.sub}</div>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {filteredGuides.length > 0 && (
                <div className="search-panel-section">
                  <h2 className="search-section-title">Related Guides</h2>
                  <div className="guide-list">
                    {filteredGuides.slice(0, 5).map((guide, idx) => (
                      <Link
                        key={idx}
                        to={guide.route}
                        className="guide-link"
                        onClick={handleResultClick}
                      >
                        <div className="guide-main">
                          <div className="guide-icon">
                            {guide.sidebar?.icon}
                          </div>
                          <div className="guide-info">
                            <div className="guide-label">
                              {guide.sidebar?.label || guide.label}
                            </div>
                            <div className="guide-description">
                              {guide.seo?.description}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
